export interface ConsoleBufferEntry {
  timestamp: string; // ISO 8601 capture time
  level: string;
  message: string;
}

type ConsoleMethod = 'log' | 'info' | 'warn' | 'error' | 'debug';

/**
 * Static ring buffer of recent console output, uncaught errors and HTTP errors.
 *
 * Deliberately NOT an @Injectable service — install() must run before Angular
 * bootstraps (see main.ts) so early boot logs are captured too. Angular's
 * default ErrorHandler logs via console.error, so wrapping console is enough
 * to capture framework errors without a custom ErrorHandler.
 */
export class ConsoleBufferService {
  private static readonly MAX_ENTRIES = 300;
  private static readonly MAX_MESSAGE_LENGTH = 2000;
  private static readonly MAX_BUFFER_BYTES = 700 * 1024;
  private static readonly MAX_STACK_LINES = 10;
  private static readonly PERSIST_KEY = 'bugReport.consoleBuffer.v1';
  private static readonly PERSIST_THROTTLE_MS = 5000;

  private static entries: ConsoleBufferEntry[] = [];
  private static installed = false;
  private static persistTimer: ReturnType<typeof setTimeout> | null = null;
  private static dirty = false;
  private static listeners: ((entry: ConsoleBufferEntry) => void)[] = [];

  /** Be told about every new entry (the playback log uploader mirrors them). */
  static subscribe(listener: (entry: ConsoleBufferEntry) => void): void {
    this.listeners.push(listener);
  }

  /**
   * Wrap console methods and register global error listeners. Idempotent.
   */
  static install(): void {
    if (this.installed || typeof window === 'undefined') {
      return;
    }
    this.installed = true;

    // Restore any buffer persisted by the previous page load BEFORE wrapping
    // console, so the reload marker precedes all new page-load logs.
    this.restorePersistedBuffer();

    const methods: ConsoleMethod[] = ['log', 'info', 'warn', 'error', 'debug'];
    methods.forEach(method => {
      const original = console[method].bind(console);
      console[method] = (...args: unknown[]) => {
        try {
          this.push(method, args);
        } catch {
          // Never let buffering break the original console call.
        }
        original(...args);
      };
    });

    window.addEventListener('error', (event: ErrorEvent) => {
      try {
        this.push('error', ['[uncaught]', event.error ?? event.message]);
      } catch {
        // Best effort only.
      }
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        this.push('error', ['[unhandledrejection]', event.reason]);
      } catch {
        // Best effort only.
      }
    });

    // Flush the buffer to sessionStorage when the page is being hidden or
    // torn down so pre-reload incident evidence survives.
    window.addEventListener('pagehide', () => this.persistNow());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.persistNow();
      }
    });
  }

  /**
   * Record a failed HTTP request (fed by HttpErrorCaptureInterceptor).
   */
  static recordHttpError(entry: { method: string; url: string; status: number; message: string }): void {
    this.push('http', [`${entry.method} ${entry.url} -> ${entry.status}: ${entry.message}`]);
  }

  /**
   * Copy of the buffer, dropping oldest entries until the serialized size
   * stays under ~700 KB.
   */
  static getBuffer(): ConsoleBufferEntry[] {
    const copy = this.entries.map(entry => ({ ...entry }));
    while (copy.length > 0 && JSON.stringify(copy).length > this.MAX_BUFFER_BYTES) {
      copy.shift();
    }
    return copy;
  }

  private static push(level: string, args: unknown[]): void {
    const message = this.sanitize(
      args.map(arg => this.serialize(arg)).join(' ')
    ).slice(0, this.MAX_MESSAGE_LENGTH);

    const entry = { timestamp: new Date().toISOString(), level, message };
    this.entries.push(entry);
    for (const listener of this.listeners) {
      try {
        listener(entry);
      } catch {
        // A listener must never break console output.
      }
    }

    if (this.entries.length > this.MAX_ENTRIES) {
      this.entries.shift();
    }

    this.dirty = true;
    this.schedulePersist();
  }

  /**
   * Restore the buffer persisted by the previous page load (if any) and mark
   * the boundary with an explicit reload marker entry.
   */
  private static restorePersistedBuffer(): void {
    try {
      const raw = sessionStorage.getItem(this.PERSIST_KEY);
      if (!raw) {
        return;
      }
      sessionStorage.removeItem(this.PERSIST_KEY); // never restore twice; a corrupt blob can never wedge boot
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return;
      }
      const restored = parsed.filter((entry): entry is ConsoleBufferEntry =>
        !!entry && typeof entry.timestamp === 'string' && typeof entry.level === 'string' && typeof entry.message === 'string');
      if (restored.length === 0) {
        return;
      }
      const lastTimestamp = restored[restored.length - 1].timestamp;
      this.entries = restored;
      this.entries.push({
        timestamp: new Date().toISOString(),
        level: 'log',
        message: `[console-buffer] ===== page reload: restored ${restored.length} entries from previous page load (last entry ${lastTimestamp}) =====`
      });
      while (this.entries.length > this.MAX_ENTRIES) {
        this.entries.shift();
      }
      this.dirty = true; // re-persist soon so a second quick reload keeps the evidence
      this.schedulePersist();
    } catch {
      try {
        sessionStorage.removeItem(this.PERSIST_KEY);
      } catch {
        // Best effort only.
      }
    }
  }

  /**
   * Coalesce steady-state writes: at most one sessionStorage write per
   * PERSIST_THROTTLE_MS while dirty. persistNow() bypasses the throttle.
   */
  private static schedulePersist(): void {
    if (this.persistTimer !== null || typeof window === 'undefined') {
      return;
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.persistNow();
    }, this.PERSIST_THROTTLE_MS);
  }

  /**
   * Synchronous write (safe from pagehide). Every sessionStorage touch is
   * try/caught — Firefox private mode can throw on access.
   */
  private static persistNow(): void {
    if (!this.dirty) {
      return;
    }
    try {
      sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify(this.entries));
      this.dirty = false;
    } catch {
      // Quota exceeded / privacy mode: retry once with the newest 100 entries, then give up.
      try {
        sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify(this.entries.slice(-100)));
        this.dirty = false;
      } catch {
        // Best effort only — stay dirty so the next flush retries.
      }
    }
  }

  private static serialize(value: unknown): string {
    if (value instanceof Error) {
      const stack = (value.stack || '').split('\n').slice(0, this.MAX_STACK_LINES).join('\n');
      return `${value.name}: ${value.message}${stack ? '\n' + stack : ''}`;
    }
    if (value === null || value === undefined || typeof value !== 'object') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  /**
   * Redact anything that looks like a credential before it hits the buffer.
   */
  private static sanitize(message: string): string {
    return message
      // Key/value secrets: access_token: "...", accessToken: "...", api_key=..., etc.
      .replace(
        /(access[_-]?token|refresh[_-]?token|client[_-]?secret|api[_-]?key)(["']?\s*[:=]\s*["']?)[^\s"',}&]+/gi,
        '$1$2[REDACTED]'
      )
      // Authorization headers.
      .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/g, 'Bearer [REDACTED]')
      // Long quoted base64/hex-ish strings (no spaces) that look like tokens.
      .replace(/"[A-Za-z0-9+/=\-._~]{121,}"/g, '"[REDACTED]"');
  }
}
